"use client";

import { Icon } from "@/components/ui/Icon";
import { motion } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CHAUFFEUR_HUB } from "./hubDict";
import imageTransfertsAeroports from "@/assets/images/chauffeur/aeroport/Transferts-aeroports-heliports.png";
import imageMiseADisposition from "@/assets/images/chauffeur/services/services/Mise-a-disposition.png";
import imageEvenements from "@/assets/images/chauffeur/events/event-soir.png";
import imageBusiness from "@/assets/images/chauffeur/services/services/Business.png";
import imageMariages from "@/assets/images/chauffeur/events/usage/Mariages.png";
import imageExcursions from "@/assets/images/chauffeur/services/Experience-passager.png";

type ServicesSectionProps = { locale?: Locale };

// Mapping titre → visuel, FR et EN (la version EN ne matche pas les libellés FR).
const resolveServiceImage = (title: string): StaticImageData => {
  const label = title.toLowerCase();
  if (label.includes("transferts") || label.includes("transfer")) {
    return imageTransfertsAeroports;
  }
  if (
    label.includes("mise à disposition") ||
    label.includes("mise a disposition") ||
    label.includes("hourly hire")
  ) {
    return imageMiseADisposition;
  }
  if (
    label.includes("événements") ||
    label.includes("evenements") ||
    label.includes("events")
  ) {
    return imageEvenements;
  }
  if (label.includes("business")) return imageBusiness;
  if (label.includes("mariages") || label.includes("weddings")) {
    return imageMariages;
  }
  return imageExcursions;
};

const ServicesSection = ({
  locale = "fr",
}: ServicesSectionProps): JSX.Element => {
  const d = tr(CHAUFFEUR_HUB, locale).services;
  const [first, ...rest] = d.items;

  return (
    <section className="relative overflow-hidden bg-white py-16 text-black md:py-20">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(202,160,45,0.12),transparent_40%)]"
        aria-hidden
      />
      <div className="relative mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.2em] text-black/55"
        >
          {d.eyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-2 font-space-age text-xl tracking-[0.2em] md:text-2xl"
        >
          {d.heading}
        </motion.h2>
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="mb-8 max-w-3xl text-sm text-black/70 md:mb-10"
        >
          {d.intro}
        </motion.p>
        <div className="grid items-start gap-5 lg:grid-cols-12">
          {first && (
            <motion.article
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.45 }}
              whileHover={{ y: -4 }}
              className="group relative self-start overflow-hidden rounded-3xl border border-black/15 bg-white p-6 shadow-[0_14px_38px_rgba(0,0,0,0.08)] lg:col-span-7 lg:p-8"
            >
              <div
                className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-gold-600 to-primary"
                aria-hidden
              />
              <div className="relative">
                <div className="mb-4 overflow-hidden rounded-2xl border border-black/10">
                  <div className="relative aspect-[21/9] w-full">
                    <Image
                      src={resolveServiceImage(first.title)}
                      alt={first.title}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="(max-width: 1024px) 100vw, 45vw"
                    />
                  </div>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-gold-800">
                  {d.mostRequested}
                </p>
                <h3 className="mt-3 text-2xl font-semibold text-black">
                  {first.title}
                </h3>
                <p className="mt-3 max-w-xl text-sm text-black/70">
                  {first.text}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold-300 bg-gold-100 px-3 py-1 text-xs text-gold-900">
                  <Icon
                    icon={first.icon || "ph:star"}
                    className="h-4 w-4 text-gold-800"
                  />
                  {d.priorityBadge}
                </div>
                <div className="mt-6 grid gap-2 sm:grid-cols-3">
                  {d.points.map((point) => (
                    <p
                      key={point}
                      className="rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-black/65"
                    >
                      {point}
                    </p>
                  ))}
                </div>
              </div>
            </motion.article>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-5">
            {rest.map((s, idx) => (
              <motion.article
                key={s.title}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.38, delay: idx * 0.05 }}
                whileHover={{ y: -3 }}
                className="group relative overflow-hidden rounded-2xl border border-black/10 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-black/20 hover:shadow-md"
              >
                <div
                  className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-primary/80 to-gold-600/50"
                  aria-hidden
                />
                <div className="mb-3 overflow-hidden rounded-xl border border-black/10">
                  <div className="relative aspect-[21/9] w-full">
                    <Image
                      src={resolveServiceImage(s.title)}
                      alt={s.title}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="(max-width: 1024px) 100vw, 30vw"
                    />
                  </div>
                </div>
                <div className="relative flex items-start gap-3 ps-2">
                  {s.icon && (
                    <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-black/10 bg-white text-black">
                      <Icon icon={s.icon} className="h-5 w-5" />
                    </span>
                  )}
                  <div>
                    <h3 className="text-base font-semibold text-black">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-black/70">{s.text}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
